'use client';

import { createContext, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import NotificationComponent from './Notification';

type NotificationsProviderProps = {
  children: React.ReactNode,
  className?: string
};

type Notification = {
  message: string,
  variant: 'success' | 'danger' | 'warning',
  id: string,
  onDismiss?: (id: string) => void,
  duration?: number
};

export type AddNotification = (notification: Notification) => void;

export const NotificationsContext = createContext<AddNotification | null>(null);

const NotificationsProvider = ({ children, className }: NotificationsProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const removeNotification = (id: string) => {
    setNotifications((prevNotifications) => (
      prevNotifications.filter((n) => n.id !== id)
    ));
  };

  const addNotification = (notification: Notification) => {
    if (notifications.some(({ id }) => id === notification.id)) {
      return;
    }
    setNotifications((prevState) => [...prevState, notification]);
    setTimeout(() => removeNotification(notification.id), notification.duration || 6000);
  };

  const renderNotifications = () => notifications.map(({
    message, variant, id, onDismiss,
  }) => (
    <NotificationComponent
      key={id}
      variant={variant}
      message={message}
      id={id}
      onClose={() => {
        removeNotification(id);
        if (onDismiss) onDismiss(id);
      }}
    />
  ));

  return (
    <NotificationsContext.Provider value={addNotification}>
      <div
        className={twMerge(
          'fixed z-50 right-0 top-0 lg:top-auto lg:bottom-0 w-full',
          className,
        )}
      >
        {renderNotifications()}
      </div>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsProvider;
